import * as THREE from 'three';
import { ASTRONOMICAL_CONSTANTS, SCALE_CONFIG } from './solarSystemData.js';

/**
 * ScaleMeter - Displays a visual scale reference in astronomical units (AU)
 * Shows a fixed reference line in the bottom-right corner with automatic AU conversion
 */
export class ScaleMeter {
    constructor(camera, container) {
        this.camera = camera;
        this.container = container;

        // Scale meter configuration
        this.config = {
            lineLength: 100, // pixels - constant reference line length
            position: {
                bottom: 20,
                right: 20,
            },
            fontSize: 12,
            fontFamily: 'monospace',
            lineColor: '#ffffff',
            textColor: '#ffffff',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            padding: 8,
            borderRadius: 4,
            updateInterval: 100, // ms - throttle updates for performance
        };

        // State tracking
        this.isVisible = true;
        this.lastUpdateTime = 0;
        this.currentAUValue = 0;

        // DOM elements
        this.scaleMeterElement = null;
        this.lineElement = null;
        this.textElement = null;

        // Event listener references for cleanup
        this._onResize = () => this.handleResize();
        this._onOrientationChange = () => setTimeout(() => this.handleResize(), 100);

        this.createScaleMeter();
        this.setupEventListeners();
    }

    /**
     * Create the scale meter DOM elements and add to container
     */
    createScaleMeter() {
        // Create main container element
        this.scaleMeterElement = document.createElement('div');
        this.scaleMeterElement.id = 'scale-meter';
        this.scaleMeterElement.style.cssText = `
            position: fixed;
            bottom: ${this.config.position.bottom}px;
            right: ${this.config.position.right}px;
            background: ${this.config.backgroundColor};
            padding: ${this.config.padding}px;
            border-radius: ${this.config.borderRadius}px;
            font-family: ${this.config.fontFamily};
            font-size: ${this.config.fontSize}px;
            color: ${this.config.textColor};
            z-index: 1000;
            user-select: none;
            pointer-events: none;
            backdrop-filter: blur(5px);
            border: 1px solid rgba(255, 255, 255, 0.2);
            min-width: 140px;
            text-align: center;
            max-height: 120px;
            overflow: hidden;
        `;

        // Create reference line element
        this.lineElement = document.createElement('div');
        this.lineElement.style.cssText = `
            width: ${this.config.lineLength}px;
            height: 2px;
            background: ${this.config.lineColor};
            margin: 0 auto 6px auto;
            position: relative;
        `;

        // Add tick marks at the ends of the line
        const leftTick = document.createElement('div');
        leftTick.style.cssText = `
            position: absolute;
            left: 0;
            top: -3px;
            width: 1px;
            height: 8px;
            background: ${this.config.lineColor};
        `;

        const rightTick = document.createElement('div');
        rightTick.style.cssText = `
            position: absolute;
            right: 0;
            top: -3px;
            width: 1px;
            height: 8px;
            background: ${this.config.lineColor};
        `;

        this.lineElement.appendChild(leftTick);
        this.lineElement.appendChild(rightTick);

        // Create text element for AU value
        this.textElement = document.createElement('div');
        this.textElement.style.cssText = `
            font-weight: bold;
            letter-spacing: 0.5px;
        `;

        // Assemble the scale meter
        this.scaleMeterElement.appendChild(this.lineElement);
        this.scaleMeterElement.appendChild(this.textElement);

        // Add to container
        if (this.container) {
            this.container.appendChild(this.scaleMeterElement);
        } else {
            document.body.appendChild(this.scaleMeterElement);
        }

        console.log('Scale meter created and added to DOM');
    }

    /**
     * Setup event listeners for responsive behavior
     */
    setupEventListeners() {
        // Handle window resize
        window.addEventListener('resize', this._onResize);

        // Handle mobile orientation change
        window.addEventListener('orientationchange', this._onOrientationChange);
    }

    /**
     * Handle window resize and mobile adaptation
     */
    handleResize() {
        if (!this.scaleMeterElement) return;

        const isMobile = window.innerWidth <= 768;

        if (isMobile) {
            // Adjust for mobile devices
            this.scaleMeterElement.style.bottom = '10px';
            this.scaleMeterElement.style.right = '10px';
            this.scaleMeterElement.style.fontSize = '11px';
            this.scaleMeterElement.style.padding = '6px';
        } else {
            // Desktop settings
            this.scaleMeterElement.style.bottom = `${this.config.position.bottom}px`;
            this.scaleMeterElement.style.right = `${this.config.position.right}px`;
            this.scaleMeterElement.style.fontSize = `${this.config.fontSize}px`;
            this.scaleMeterElement.style.padding = `${this.config.padding}px`;
        }
    }

    /**
     * Update the scale meter with current camera distance
     * Should be called in the animation loop
     */
    update(cameraDistance) {
        if (!this.isVisible || !this.scaleMeterElement) return;

        const currentTime = performance.now();

        // Throttle updates for performance
        if (currentTime - this.lastUpdateTime < this.config.updateInterval) {
            return;
        }

        this.lastUpdateTime = currentTime;

        // Calculate AU distance for the reference line
        const auDistance = this.calculateAUDistance(this.config.lineLength, cameraDistance);

        // Only update if the value has changed significantly
        if (Math.abs(auDistance - this.currentAUValue) > this.currentAUValue * 0.05) {
            this.currentAUValue = auDistance;
            this.updateDisplay(auDistance);
        }
    }

    /**
     * Calculate the AU distance represented by a pixel distance at current camera zoom
     * @param {number} pixelDistance - Distance in pixels
     * @param {number} cameraDistance - Current camera distance from target
     * @returns {number} Distance in astronomical units
     */
    calculateAUDistance(pixelDistance, cameraDistance) {
        if (!this.camera || !cameraDistance) return 0;

        // Get camera field of view in radians
        const fov = (this.camera.fov * Math.PI) / 180;

        // Calculate world distance represented by pixel distance
        // This uses the perspective projection formula
        const worldDistance =
            (pixelDistance * cameraDistance * Math.tan(fov / 2)) / (window.innerHeight / 2);

        // Convert from scaled world units to real kilometers
        // The scale factor depends on how the simulation scales distances
        const realDistanceKm =
            (worldDistance / SCALE_CONFIG.proportionalSizing.baseOrbitalDistance) *
            SCALE_CONFIG.proportionalSizing.baseOrbitalRadius;

        // Convert kilometers to astronomical units
        const auDistance = realDistanceKm / ASTRONOMICAL_CONSTANTS.AU;

        return auDistance;
    }

    /**
     * Update the display with the new AU value
     * @param {number} auDistance - Distance in astronomical units
     */
    updateDisplay(auDistance) {
        if (!this.textElement) return;

        // Convert AU to kilometers for other unit calculations
        const kmDistance = auDistance * ASTRONOMICAL_CONSTANTS.AU;
        const lightYearDistance = kmDistance / ASTRONOMICAL_CONSTANTS.LIGHT_YEAR;
        const parsecDistance = kmDistance / ASTRONOMICAL_CONSTANTS.PARSEC;

        // Format AU display
        let auText;
        if (auDistance >= 1000) {
            auText = `${(auDistance / 1000).toFixed(1)}k AU`;
        } else if (auDistance >= 100) {
            auText = `${auDistance.toFixed(0)} AU`;
        } else if (auDistance >= 10) {
            auText = `${auDistance.toFixed(1)} AU`;
        } else if (auDistance >= 1) {
            auText = `${auDistance.toFixed(2)} AU`;
        } else if (auDistance >= 0.1) {
            auText = `${auDistance.toFixed(3)} AU`;
        } else if (auDistance >= 0.001) {
            auText = `${auDistance.toExponential(2)} AU`;
        } else {
            auText = '< 0.001 AU';
        }

        // Format kilometers display
        let kmText;
        if (kmDistance >= 1e12) {
            kmText = `${(kmDistance / 1e12).toFixed(1)}T km`;
        } else if (kmDistance >= 1e9) {
            kmText = `${(kmDistance / 1e9).toFixed(1)}G km`;
        } else if (kmDistance >= 1e6) {
            kmText = `${(kmDistance / 1e6).toFixed(1)}M km`;
        } else if (kmDistance >= 1e3) {
            kmText = `${(kmDistance / 1e3).toFixed(1)}k km`;
        } else {
            kmText = `${kmDistance.toFixed(0)} km`;
        }

        // Format light years display
        let lyText;
        if (lightYearDistance >= 1000) {
            lyText = `${(lightYearDistance / 1000).toFixed(2)}k ly`;
        } else if (lightYearDistance >= 1) {
            lyText = `${lightYearDistance.toFixed(3)} ly`;
        } else if (lightYearDistance >= 0.001) {
            lyText = `${(lightYearDistance * 1000).toFixed(1)}m ly`;
        } else {
            lyText = `${lightYearDistance.toExponential(2)} ly`;
        }

        // Format parsecs display
        let pcText;
        if (parsecDistance >= 1000) {
            pcText = `${(parsecDistance / 1000).toFixed(2)}k pc`;
        } else if (parsecDistance >= 1) {
            pcText = `${parsecDistance.toFixed(3)} pc`;
        } else if (parsecDistance >= 0.001) {
            pcText = `${(parsecDistance * 1000).toFixed(1)}m pc`;
        } else {
            pcText = `${parsecDistance.toExponential(2)} pc`;
        }

        // Create multi-line display
        this.textElement.innerHTML = `
            <div style="line-height: 1.2; font-size: 11px;">
                <div style="font-weight: bold; margin-bottom: 2px;">${auText}</div>
                <div style="opacity: 0.8;">${kmText}</div>
                <div style="opacity: 0.8;">${lyText}</div>
                <div style="opacity: 0.8;">${pcText}</div>
            </div>
        `;
    }

    /**
     * Show the scale meter
     */
    show() {
        this.isVisible = true;
        if (this.scaleMeterElement) {
            this.scaleMeterElement.style.display = 'block';
        }
    }

    /**
     * Hide the scale meter
     */
    hide() {
        this.isVisible = false;
        if (this.scaleMeterElement) {
            this.scaleMeterElement.style.display = 'none';
        }
    }

    /**
     * Toggle scale meter visibility
     */
    toggle() {
        if (this.isVisible) {
            this.hide();
        } else {
            this.show();
        }
    }

    /**
     * Check if scale meter is currently visible
     * @returns {boolean} True if visible
     */
    isShowing() {
        return this.isVisible;
    }

    /**
     * Get current AU value being displayed
     * @returns {number} Current AU distance
     */
    getCurrentAU() {
        return this.currentAUValue;
    }

    /**
     * Set the reference line length
     * @param {number} length - New line length in pixels
     */
    setLineLength(length) {
        this.config.lineLength = Math.max(50, Math.min(200, length)); // Clamp between 50-200px

        if (this.lineElement) {
            this.lineElement.style.width = `${this.config.lineLength}px`;
        }
    }

    /**
     * Set the position of the scale meter
     * @param {number} bottom - Distance from bottom in pixels
     * @param {number} right - Distance from right in pixels
     */
    setPosition(bottom, right) {
        this.config.position.bottom = bottom;
        this.config.position.right = right;

        if (this.scaleMeterElement) {
            this.scaleMeterElement.style.bottom = `${bottom}px`;
            this.scaleMeterElement.style.right = `${right}px`;
        }
    }

    /**
     * Update styling for different themes or preferences
     * @param {Object} styleOptions - Style configuration object
     */
    updateStyle(styleOptions = {}) {
        const {
            lineColor = this.config.lineColor,
            textColor = this.config.textColor,
            backgroundColor = this.config.backgroundColor,
            fontSize = this.config.fontSize,
        } = styleOptions;

        this.config.lineColor = lineColor;
        this.config.textColor = textColor;
        this.config.backgroundColor = backgroundColor;
        this.config.fontSize = fontSize;

        if (this.scaleMeterElement) {
            this.scaleMeterElement.style.backgroundColor = backgroundColor;
            this.scaleMeterElement.style.color = textColor;
            this.scaleMeterElement.style.fontSize = `${fontSize}px`;
        }

        if (this.lineElement) {
            this.lineElement.style.backgroundColor = lineColor;
            // Update tick marks
            const ticks = this.lineElement.querySelectorAll('div');
            ticks.forEach((tick) => {
                tick.style.backgroundColor = lineColor;
            });
        }
    }

    /**
     * Get scale meter state for debugging or persistence
     * @returns {Object} Current state
     */
    getState() {
        return {
            isVisible: this.isVisible,
            currentAUValue: this.currentAUValue,
            config: { ...this.config },
            position: {
                bottom: this.config.position.bottom,
                right: this.config.position.right,
            },
        };
    }

    /**
     * Dispose of the scale meter and cleanup
     */
    dispose() {
        // Remove event listeners
        window.removeEventListener('resize', this._onResize);
        window.removeEventListener('orientationchange', this._onOrientationChange);

        // Remove DOM element
        if (this.scaleMeterElement && this.scaleMeterElement.parentNode) {
            this.scaleMeterElement.parentNode.removeChild(this.scaleMeterElement);
        }

        // Clear references
        this.scaleMeterElement = null;
        this.lineElement = null;
        this.textElement = null;
        this.camera = null;
        this.container = null;

        console.log('Scale meter disposed');
    }
}
